import {Component, Input, OnInit} from '@angular/core';
import {Dad, DadSubscriberData} from '../../../models/dad';
import {FormControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {DadService} from '../../../services/dad.service';
import {Pop} from '../../../models/pop';
import {ThresholdExplanationComponent} from '../threshold-explanation/threshold-explanation.component';
import {DialogService, DynamicDialogRef} from 'primeng/dynamicdialog';

@Component({
  selector: 'app-ice-report',
  templateUrl: './ice-report.component.html',
  styleUrls: ['../dad-form/dad-form.component.scss', './ice-report.component.scss']
})
export class IceReportComponent implements OnInit {

  @Input() currentDad: Dad;
  @Input() previousMonthSubscriptionData: DadSubscriberData;
  @Input() originalClientDeclarationMode = false;
  seeOriginalClientDeclaration = false;
  dadDeclarationForm: UntypedFormGroup;
  seePreviousMonthDeclaration = false;
  errors = false;
  successDeclaration = false;
  dadFields = ['terminal', 'handheld', 'wallboard'];
  countColsArray = [];
  colsXArray = [];
  declarationError = false;
  serverError = false;
  expiredError = false;
  recentlyDeconfiguredPopsArray = [];
  loading = true;
  disabledArray = [];

  constructor(
    private formBuilder: UntypedFormBuilder,
    public dialogService: DialogService,
    private dadService: DadService) {
  }

  ngOnInit() {
    this.seeOriginalClientDeclaration = this.originalClientDeclarationMode;
    this.dadService.getRecentlyDisabledPops(this.currentDad.uniqid).subscribe(
      res => {
        this.recentlyDeconfiguredPopsArray = res;
        this.initializeMarketsArrays();
        this.createFormGroup();
      }
    );
  }

  createFormGroup() {
    this.dadDeclarationForm = this.formBuilder.group(
      {
        authorizationCheckbox: [this.isDeclared, Validators.requiredTrue],
        confirmationCheckbox: [this.isDeclared, Validators.requiredTrue],
        restrictionCheckbox: [this.isDeclared, Validators.requiredTrue],
        thresholdExplanation: [''],
      },
    );
    for (const market of this.currentDad.markets) {
      for (const field of this.dadFields) {
        if (this.currentDad.isDadFormNewStyle) {
          for (const pop of this.currentDad.decodedPopList) {
            if (this.colsXArray[market + pop.name] === 'X') {
              this.dadDeclarationForm.addControl(
                market + ' ' + field + ' ' + pop.display_id,
                new UntypedFormControl({
                  value: this.getFormControlValue(market, field, pop.display_id),
                  disabled: this.disabledArray[market]
                })
              );
            }
          }
        } else {
          this.dadDeclarationForm.addControl(
            market + ' ' + field,
            new FormControl({
              value: this.getFormControlValue(market, field),
              disabled: this.disabledArray[market]
            })
          );
        }
      }
    }
    this.loading = false;
    if (this.originalClientDeclarationMode) {
      this.dadDeclarationForm.disable();
    }
  }

  scrollToElement($element): void {
    $element.scrollIntoView({behavior: 'smooth', block: 'start', inline: 'nearest'});
  }

  get previousUsageMonth() {
    if (!this.currentDad) {
      return;
    }
    const currentUsageMonth = new Date(this.currentDad.usageMonth);
    currentUsageMonth.setMonth(currentUsageMonth.getMonth() - 1);
    return currentUsageMonth;
  }

  getMarketName(market: string): string {
    if (market.toLowerCase().includes('liffe')) {
      return 'ICE Futures Europe Liffe';
    }
    return market;
  }

  private initializeMarketsArrays() {
    for (const market of this.currentDad.markets) {
      this.countColsArray[market] = 0;
      this.disabledArray[market] = true;
      for (const pop of this.currentDad.decodedPopList) {
        this.marketExistsForPop(market, pop);
      }
    }
  }

  marketExistsForPop(market: string, pop: Pop) {
    if (this.currentDad.dadItems[market]
      && this.currentDad.dadItems[market].length
      && this.currentDad.dadItems[market].includes(pop.display_id)) {
      if (this.recentlyDeconfiguredPopsArray[market + pop.name] === false) {
        this.countColsArray[market] += 1;
      }
      this.disabledArray[market] = false;
      this.colsXArray[market + pop.name] = 'X';
      return;
    }
    this.colsXArray[market + pop.name] = '';
  }

  private getFormControlValue(market: string, field: string, pop?: string) {
    if (this.disabledArray[market]) {
      return 0;
    }
    const detailsNumber = this.dadFields.indexOf(field);
    if (pop) {
      // Dad form is on new style.
      if (!this.currentDad.decodedSubscriberData
        || !this.currentDad.decodedSubscriberData.declarations.filter(declaration => declaration.market_name === market)[0]) {
        // Client did not declare yet.
        if (!this.previousMonthSubscriptionData || !this.previousMonthSubscriptionData.declarations
          || this.previousMonthSubscriptionData.declarations.filter(declaration => declaration.market_name === market).length === 0
          || !this.previousMonthSubscriptionData.declarations.filter(declaration => declaration.market_name === market)[0]
          || !this.previousMonthSubscriptionData.declarations.filter(declaration => declaration.market_name === market)[0].details[detailsNumber][pop]) {
          // There is no previous declaration
          return 0;
        }
        return this.previousMonthSubscriptionData.declarations
          .filter(declaration => declaration.market_name === market)[0].details[detailsNumber][pop];
      }
      return this.currentDad.decodedSubscriberData.declarations
        .filter(declaration => declaration.market_name === market)[0].details[detailsNumber][pop];
    } else {
      if (!this.currentDad.decodedSubscriberData
        || !this.currentDad.decodedSubscriberData.declarations.filter(declaration => declaration.market_name === market)[0]) {
        // Client did not declare yet.
        if (!this.previousMonthSubscriptionData || !this.previousMonthSubscriptionData.declarations
          || !this.previousMonthSubscriptionData.declarations.filter(declaration => declaration.market_name === market)[0]) {
          // There is no previous declaration
          return 0;
        }
        return this.previousMonthSubscriptionData.declarations
          .filter(declaration => declaration.market_name === market)[0].details[detailsNumber];
      }
      return this.currentDad.decodedSubscriberData.declarations
        .filter(declaration => declaration.market_name === market)[0].details[detailsNumber];
    }
  }

  toggleSeePreviousMonthDeclaration() {
    this.seePreviousMonthDeclaration = !this.seePreviousMonthDeclaration;
  }

  toggleSeeOriginalClientDeclaration() {
    this.seeOriginalClientDeclaration = !this.seeOriginalClientDeclaration;
  }

  previousMonthDeclarationValue(market: string, field: string) {
    if (this.currentDad.isDadFormNewStyle && this.previousMonthSubscriptionData.isDadFormNewStyle === this.currentDad.isDadFormNewStyle) {
      return this.previousMonthDeclarationValueForNewForm(market, field);
    }
    if (!this.previousMonthSubscriptionData.declarations.filter(declaration => declaration.market_name === market)[0]) {
      return 0;
    }
    const detailsNumber = this.dadFields.indexOf(field);
    return this.previousMonthSubscriptionData.declarations
      .filter(declaration => declaration.market_name === market)[0].details[detailsNumber];
  }

  originalClientDeclarationValue(market: string, field: string) {
    if (!this.currentDad.decodedOriginalClientDeclaration.declarations.filter(declaration => declaration.market_name === market)[0]) {
      return 0;
    }
    const detailsNumber = this.dadFields.indexOf(field);
    const declarationArray = this.currentDad.decodedOriginalClientDeclaration.declarations
      .filter(declaration => declaration.market_name === market)[0].details[detailsNumber];
    let sum = 0;
    // @ts-ignore
    // tslint:disable-next-line:forin
    for (const declaration in declarationArray) {
      // @ts-ignore
      sum += declarationArray[declaration];
    }
    return sum;
  }

  previousMonthDeclarationValueForNewForm(market: string, field: string) {
    if (!this.previousMonthSubscriptionData.declarations.filter(declaration => declaration.market_name === market)[0]) {
      return 0;
    }
    const detailsNumber = this.dadFields.indexOf(field);
    const declarationArray = this.previousMonthSubscriptionData.declarations
      .filter(declaration => declaration.market_name === market)[0].details[detailsNumber];
    let sum = 0;
    // @ts-ignore
    // tslint:disable-next-line:forin
    for (const declaration in declarationArray) {
      // @ts-ignore
      sum += declarationArray[declaration];
    }
    return sum;
  }

  onSubmit(withWarning = true) {
    if (withWarning && !confirm('Do you really want to submit this declaration?')) {
      return;
    }
    this.declarationError = false;
    for (const market of this.currentDad.markets) {
      if (this.currentDad.isDadFormNewStyle) {
        this.currentDad.decodedPopList.forEach(pop => {
          if (this.dadDeclarationForm.get(market + ' terminal' + ' ' + pop.display_id) && this.dadDeclarationForm.get(market + ' terminal' + ' ' + pop.display_id).value < 1) {
            this.declarationError = true;
            this.successDeclaration = false;
            window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
          }
        });
      } else if (this.dadDeclarationForm.get(market + ' terminal').value < this.countColsArray[market]) {
        this.declarationError = true;
        this.successDeclaration = false;
        window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
        return;
      }
    }
    if (this.declarationError) {
      return;
    }
    if (!this.dadDeclarationForm.valid) {
      this.errors = true;
      this.successDeclaration = false;
      window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
      return;
    }
    this.dadService.submitDadFormDeclaration(this.currentDad.uniqid, this.dadDeclarationForm.getRawValue()).subscribe({
        next: () => {
          this.errors = false;
          this.serverError = false;
          this.declarationError = false;
          this.successDeclaration = true;
          this.currentDad.largeCountDifferenceExplanation = this.dadDeclarationForm.get('thresholdExplanation').value;
          this.dadDeclarationForm.get('thresholdExplanation').setValue('');
          window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
        },
        error: error => {
          if (error.status === 401) {
            this.expiredError = true;
          } else if (error.status === 406) {
            const ref = this.openThresholdExplanationPopUp();
            this.closeThresholdExplanationPopUp(ref);
            return;
          } else {
            this.serverError = true;
          }
          window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
        }
      }
    );
  }

  get previousMonthDeclarationBtnLabel() {
    if (this.seePreviousMonthDeclaration) {
      return 'Hide declared count for previous month';
    }
    return 'See declared count for previous month';
  }

  get originalClientDeclarationBtnLabel() {
    if (this.seeOriginalClientDeclaration) {
      return 'Hide the client\'s original reported count';
    }
    return 'See the client\'s original reported count';
  }

  get isDeclared() {
    return !!(this.currentDad && this.currentDad.decodedSubscriberData);
  }

  getTotalValueForMarketAndField(market: string, field: string) {
    let totalValue = 0;
    this.currentDad.decodedPopList.forEach(pop => {
      if (this.dadDeclarationForm.get(market + ' ' + field + ' ' + pop.display_id)) {
        totalValue += this.dadDeclarationForm.get(market + ' ' + field + ' ' + pop.display_id).value;
      }
    });
    return totalValue;
  }

  private openThresholdExplanationPopUp() {
    return this.dialogService.open(ThresholdExplanationComponent, {
      showHeader: true,
      header: 'Sudden change in declaration detected',
      width: '30%',
      style: {'max-height': '100%'},
      dismissableMask: true,
      data: {},
    });
  }

  closeThresholdExplanationPopUp(ref: DynamicDialogRef) {
    ref.onClose.subscribe(res => {
      if (!res) {
        return;
      }
      this.dadDeclarationForm.get('thresholdExplanation').setValue(res);
      this.onSubmit(false);
    });
  }
}
